import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { User, MapPin, Phone, Copy } from 'lucide-react';

interface SenderSectionProps {
  formData: {
    senderAddressType: 'new' | 'existing';
    sender: string;
    senderPhone: string;
    senderProvince: string;
    senderDistrict: string;
    senderWard: string;
    senderDetailedAddress: string;
  };
  selectedSenderAddress: any;
  mockAddresses: any[];
  onInputChange: (field: string, value: string) => void;
  onAddressSelect: (address: any, type: 'sender' | 'recipient') => void;
}

/**
 * Component quản lý thông tin người gửi
 * Hỗ trợ chọn từ danh sách có sẵn hoặc nhập mới
 */
const SenderSection: React.FC<SenderSectionProps> = ({
  formData,
  selectedSenderAddress,
  mockAddresses,
  onInputChange,
  onAddressSelect
}) => {
  return (
    <Card className="border-l-4 border-l-green-500 shadow-lg hover:shadow-xl transition-all duration-300 group">
      <CardHeader className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-950/30 dark:to-green-900/20 pb-3">
        <CardTitle className="text-base text-green-900 dark:text-green-100 flex items-center">
          <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg mr-3 group-hover:scale-110 transition-transform duration-200">
            <User className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <div className="font-semibold">Người gửi</div>
            <div className="text-xs text-green-700 dark:text-green-300 font-normal">Chọn từ danh sách hoặc nhập mới</div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        <div className="space-y-3">
          {/* <Label className="text-sm font-medium">Chọn người gửi</Label> */}
          <Tabs 
            value={formData.senderAddressType || 'existing'} 
            onValueChange={(value) => onInputChange('senderAddressType', value)}
          >
            <TabsList className="grid w-full grid-cols-2 bg-green-50 dark:bg-green-950/20">
              <TabsTrigger value="existing" className="data-[state=active]:bg-green-100 data-[state=active]:text-green-900">Từ danh sách</TabsTrigger>
              <TabsTrigger value="new" className="data-[state=active]:bg-green-100 data-[state=active]:text-green-900">Nhập mới</TabsTrigger>
            </TabsList>
            
            <TabsContent value="existing" className="space-y-3">
              <Select 
                value={selectedSenderAddress?.id || ''}
                onValueChange={(value) => {
                  const address = mockAddresses.find(addr => addr.id === value);
                  if (address) onAddressSelect(address, 'sender');
                }}
              >
                <SelectTrigger className="h-11 border-green-200 focus:border-green-400 focus:ring-green-400/20">
                  <SelectValue placeholder="Chọn người gửi từ danh sách">
                    {selectedSenderAddress ? selectedSenderAddress.name : "Chọn người gửi từ danh sách"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {mockAddresses.map((address) => (
                    <SelectItem key={address.id} value={address.id}>
                      {address.name} - {address.phone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Hiển thị thông tin đã chọn */}
              {selectedSenderAddress && (
                <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-green-600" />
                      <span className="font-medium text-green-900 dark:text-green-100">{selectedSenderAddress.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-green-600" />
                      <span className="text-green-700 dark:text-green-300">{selectedSenderAddress.phone}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-green-700 dark:text-green-300">{selectedSenderAddress.fullAddress}</span>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="new" className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="sender" className="text-sm font-medium flex items-center gap-1">
                    Tên người gửi 
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="sender"
                    value={formData.sender}
                    onChange={(e) => onInputChange('sender', e.target.value)}
                    placeholder="Nhập tên người gửi"
                    className="h-11 border-green-200 focus:border-green-400 focus:ring-green-400/20"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="senderPhone" className="text-sm font-medium flex items-center gap-1">
                    Số điện thoại 
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="senderPhone"
                    value={formData.senderPhone}
                    onChange={(e) => onInputChange('senderPhone', e.target.value)}
                    placeholder="Nhập số điện thoại"
                    className="h-11 border-green-200 focus:border-green-400 focus:ring-green-400/20"
                    required
                  />
                </div>
              </div>

              {/* Địa chỉ chi tiết người gửi */}
              <div className="space-y-3">
                <Label className="text-sm font-medium flex items-center gap-1">
                  Địa chỉ người gửi 
                  <span className="text-red-500">*</span>
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="senderProvince" className="text-xs text-muted-foreground">Tỉnh/Thành phố</Label>
                    <Select value={formData.senderProvince} onValueChange={(value) => onInputChange('senderProvince', value)}>
                      <SelectTrigger className="h-11 border-green-200 focus:border-green-400 focus:ring-green-400/20">
                        <SelectValue placeholder="Chọn tỉnh/thành" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TP.HCM">TP.HCM</SelectItem>
                        <SelectItem value="Hà Nội">Hà Nội</SelectItem>
                        <SelectItem value="Đà Nẵng">Đà Nẵng</SelectItem>
                        <SelectItem value="Cần Thơ">Cần Thơ</SelectItem>
                        <SelectItem value="Hải Phòng">Hải Phòng</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="senderDistrict" className="text-xs text-muted-foreground">Quận/Huyện</Label>
                    <Select value={formData.senderDistrict} onValueChange={(value) => onInputChange('senderDistrict', value)}>
                      <SelectTrigger className="h-11 border-green-200 focus:border-green-400 focus:ring-green-400/20">
                        <SelectValue placeholder="Chọn quận/huyện" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Quận 1">Quận 1</SelectItem>
                        <SelectItem value="Quận 3">Quận 3</SelectItem>
                        <SelectItem value="Quận 10">Quận 10</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="senderWard" className="text-xs text-muted-foreground">Phường/Xã</Label>
                    <Select value={formData.senderWard} onValueChange={(value) => onInputChange('senderWard', value)}>
                      <SelectTrigger className="h-11 border-green-200 focus:border-green-400 focus:ring-green-400/20">
                        <SelectValue placeholder="Chọn phường/xã" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Phường 1">Phường 1</SelectItem>
                        <SelectItem value="Phường 2">Phường 2</SelectItem>
                        <SelectItem value="Phường 3">Phường 3</SelectItem>
                        <SelectItem value="Phường 4">Phường 4</SelectItem>
                        <SelectItem value="Phường 5">Phường 5</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="senderDetailedAddress" className="text-sm font-medium">Địa chỉ chi tiết</Label>
                  <Input
                    id="senderDetailedAddress"
                    value={formData.senderDetailedAddress}
                    onChange={(e) => onInputChange('senderDetailedAddress', e.target.value)}
                    placeholder="VD: 456 Lê Văn B, Khu phố 2"
                    className="h-11 border-green-200 focus:border-green-400 focus:ring-green-400/20"
                    required
                  />
                </div>
              </div>

              {/* Validation feedback */}
              {(formData.sender || formData.senderPhone) && (
                <div className="bg-green-50 dark:bg-green-950/20 p-3 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-green-700 dark:text-green-300">Thông tin người gửi đã được nhập</span>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </CardContent>
    </Card>
  );
};

export default SenderSection;